/**
 * Shared helpers for client-side full-table export (paginated APIs).
 *
 * ASSUMPTION: 10k rows keeps SheetJS + browser heap workable on typical
 * laptops; larger exports should use a backend stream later.
 */

export const EXPORT_MAX_ROWS = 10_000;
export const EXPORT_PAGE_SIZE = 100;

export interface FetchPageResult<T> {
  content: T[];
  totalElements?: number;
}

export interface FetchAllPagesForExportOptions<T> {
  fetchPage: (page: number, size: number) => Promise<FetchPageResult<T>>;
  maxRows?: number;
  pageSize?: number;
}

export interface FetchAllPagesForExportResult<T> {
  items: T[];
  truncated: boolean;
  totalElements: number;
}

/**
 * Walks API pages until maxRows or empty page.
 * Does not touch React state.
 */
export async function fetchAllPagesForExport<T>({
  fetchPage,
  maxRows = EXPORT_MAX_ROWS,
  pageSize = EXPORT_PAGE_SIZE,
}: FetchAllPagesForExportOptions<T>): Promise<FetchAllPagesForExportResult<T>> {
  const items: T[] = [];
  let page = 1;
  let totalElements = 0;

  while (items.length < maxRows) {
    const remaining = maxRows - items.length;
    const size = Math.min(pageSize, remaining);
    const result = await fetchPage(page, size);
    const batch = result.content ?? [];

    if (typeof result.totalElements === "number") {
      totalElements = result.totalElements;
    }

    if (batch.length === 0) {
      break;
    }

    items.push(...batch);

    if (batch.length < size) {
      break;
    }

    page += 1;
  }

  if (totalElements <= 0) {
    totalElements = items.length;
  }

  const truncated = items.length >= maxRows && totalElements > items.length;

  return {
    items,
    truncated,
    totalElements,
  };
}
